import { useEffect, useId, useRef, useState, type RefObject } from "react";
import { WHATSAPP_AGILE_CODE_PATTERN, type WhatsAppSenderPerson } from "@gioia365/contracts";
import { Button } from "../../design/components/Button";
import { Icon } from "../../design/components/Icon";
import { Reason } from "../../design/components/Reason";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { useLocale, useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { readAgilePairing } from "../../whatsapp/settings-api";
import type { Feedback } from "./draft-dialogs";

// WhatsApp Agile in Impostazioni → «Invio dei WhatsApp» (piano M6, R9, Task 27b; WA-1, WA-3; mock-up `whatsapp.html`, `#m-wa-setup-qr`,
// e `mockup.js`, `bindWaSetup`): la parte di Agile della finestra «Imposta» e l'avviso della pagina quando un canale non consegna.
//
// - Il codice del numero su Agile, come lo dà il pannello del fornitore; incollato il link intero, il codice si ricava da lì (come in
//   GioiaFlow). Il server lo vuole nella forma dei codici (`WHATSAPP_AGILE_CODE_PATTERN`), e il campo lo dice prima di chiedere.
// - Il codice QR è la pagina del fornitore per quel codice (`agileSignup.pairingBaseUrl` seguito dal codice appena scritto): si apre in
//   una scheda nuova, mai dentro la finestra, perché incorporata la pagina del fornitore non tiene la sua sessione e il telefono non si
//   collega (GioiaFlow, 5 agosto 2026). Accanto, i passi del mock-up; il terzo dice della scheda nuova.
// - Una persona già su Agile si collega di nuovo con lo stesso codice senza vederlo: «Collega di nuovo il telefono» legge il codice QR
//   dal server (`GET …/agile-pairing`), avvisa che il fornitore manda subito i messaggi che teneva in coda (`mayReleaseQueued`, con
//   quanti), e «Apri il codice QR» lo apre con `window.open`, così il link non sta nella pagina. «Ho inquadrato il codice, salva» rimanda
//   lo stesso codice, che il server ricontrolla. Il codice resta soltanto nello stato di questa finestra: chiusa la finestra, non c'è più.
// - Il codice QR salvato lo legge soltanto chi ha «Invio dei WhatsApp» (o Gioia365, `canRepair`): il server non lo allarga a chi
//   imposta con «Gestire il team» (revisione finale di R9, I2), perché leggere un codice salvato da altri è più che scriverne uno che si
//   conosce. A chi non l'ha il pulsante non si mostra, e i suggerimenti dicono di scrivere di nuovo il codice.

/** Il codice di Agile da ciò che si è scritto o incollato: dal link intero (`…/msg_auth/<codice>`) il codice, altrimenti il testo. */
export function agileCodeOf(input: string): string {
  const trimmed = input.trim();
  return /\/msg_auth\/([^/?#\s]+)/.exec(trimmed)?.[1] ?? trimmed;
}

/** Il collegamento di nuovo dello stesso telefono, dal codice QR letto dal server. */
type Repair =
  | { phase: "idle" }
  | { phase: "loading" }
  | { phase: "ready"; code: string; pairingUrl: string; recentlyTaken: number; mayReleaseQueued: boolean }
  | { phase: "failed"; problem: Feedback };

export type AgileSetup = {
  /** La persona era già su Agile: il codice salvato non si mostra, e per chi lo può leggere c'è «Collega di nuovo il telefono». */
  saved: boolean;
  /** Chi guarda può leggere il codice QR salvato («Invio dei WhatsApp», o Gioia365): il server lo chiede uguale. */
  canRepair: boolean;
  code: string;
  setCode: (value: string) => void;
  codeError: Feedback | null;
  setCodeError: (error: Feedback | null) => void;
  /** Il link del codice QR del codice scritto, quando è nella forma dei codici e il server dice dove sta. */
  qrUrl: string | null;
  repair: Repair;
  startRepair: () => void;
  /** Il codice da salvare (scritto, o letto con «Collega di nuovo il telefono»); `null`, con l'errore sul campo, se manca o è sbagliato. */
  codeForSave: () => string | null;
};

type SetupOptions = {
  tab: TabContext;
  person: WhatsAppSenderPerson;
  pairingBaseUrl: string | null;
  /** Il campo del codice, per riportarci il cursore dopo un errore (lo stesso ref va a `AgileSetupFields`). */
  codeRef: RefObject<HTMLInputElement | null>;
  /** Se chi guarda può leggere il codice QR salvato (vedi in cima al file). */
  canRepair: boolean;
};

/** Lo stato della parte di Agile della finestra «Imposta» per questa persona. */
export function useAgileSetup({ tab, person, pairingBaseUrl, codeRef, canRepair }: SetupOptions): AgileSetup {
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<Feedback | null>(null);
  const [repair, setRepair] = useState<Repair>({ phase: "idle" });
  const reading = useRef<AbortController | null>(null);
  const saved = person.sender?.channel === "agile";
  const typed = agileCodeOf(code);
  const qrUrl = pairingBaseUrl !== null && WHATSAPP_AGILE_CODE_PATTERN.test(typed) ? `${pairingBaseUrl}${typed}` : null;

  // Chiusa la finestra, la lettura del codice QR ancora in viaggio non serve più.
  useEffect(() => () => reading.current?.abort(), []);

  function startRepair() {
    if (!canRepair || repair.phase === "loading") return;
    reading.current?.abort();
    const controller = new AbortController();
    reading.current = controller;
    setRepair({ phase: "loading" });
    readAgilePairing(tab, person.membershipId, controller.signal).then(
      (response) => {
        const found = agileCodeOf(response.pairingUrl);
        if (controller.signal.aborted) return;
        if (!WHATSAPP_AGILE_CODE_PATTERN.test(found)) {
          setRepair({ phase: "failed", problem: { text: (tr) => tr("errors.internal_error") } });
          return;
        }
        setRepair({ phase: "ready", code: found, pairingUrl: response.pairingUrl, recentlyTaken: response.recentlyTaken, mayReleaseQueued: response.mayReleaseQueued });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setRepair({ phase: "failed", problem: { text: (tr) => messageForError(tr, error) } });
      },
    );
  }

  function codeForSave(): string | null {
    if (typed !== "") {
      if (!WHATSAPP_AGILE_CODE_PATTERN.test(typed)) {
        setCodeError({ text: (tr) => tr("whatsappSendingSettings.agile.codeInvalid") });
        codeRef.current?.focus();
        return null;
      }
      setCodeError(null);
      return typed;
    }
    if (repair.phase === "ready") {
      setCodeError(null);
      return repair.code;
    }
    let missing: "codeMissing" | "codeMissingSaved" | "codeMissingSavedNoRepair" = "codeMissing";
    if (saved) missing = canRepair ? "codeMissingSaved" : "codeMissingSavedNoRepair";
    setCodeError({ text: (tr) => tr(`whatsappSendingSettings.agile.${missing}`) });
    codeRef.current?.focus();
    return null;
  }

  return { saved, canRepair, code, setCode, codeError, setCodeError, qrUrl, repair, startRepair, codeForSave };
}

type FieldsProps = { setup: AgileSetup; codeRef: RefObject<HTMLInputElement | null>; id: string; busy: boolean; saving: boolean };

/**
 * Il codice, i passi del codice QR, «Apri il codice QR» e, per chi era già su Agile, «Collega di nuovo il telefono» (soltanto a chi può
 * leggere il codice QR salvato).
 */
export function AgileSetupFields({ setup, codeRef, id, busy, saving }: FieldsProps) {
  const t = useT();
  const ids = useId();
  const { repair, qrUrl } = setup;
  const typed = agileCodeOf(setup.code);
  const whyClosedId = `${ids}-why`;
  let openQr;
  if (qrUrl !== null) {
    openQr = (
      <a className="g-button" data-variant="primary" href={qrUrl} target="_blank" rel="noopener noreferrer">
        {t("whatsappSendingSettings.agile.openQr")}
      </a>
    );
  } else if (repair.phase === "ready") {
    const { pairingUrl } = repair;
    openQr = (
      <Button variant="primary" onClick={() => window.open(pairingUrl, "_blank", "noopener,noreferrer")}>
        {t("whatsappSendingSettings.agile.openQr")}
      </Button>
    );
  } else {
    openQr = (
      <>
        <Button disabled aria-describedby={whyClosedId}>
          {t("whatsappSendingSettings.agile.openQr")}
        </Button>
        <span id={whyClosedId} className="g-wa-muted">
          {typed === "" ? t("whatsappSendingSettings.agile.openQrFirst") : t("whatsappSendingSettings.agile.codeInvalid")}
        </span>
      </>
    );
  }
  let hint = t("whatsappSendingSettings.agile.codeHint");
  if (setup.saved) hint = setup.canRepair ? t("whatsappSendingSettings.agile.codeSavedHint") : t("whatsappSendingSettings.agile.codeSavedHintNoRepair");
  let release: string | null = null;
  if (repair.phase === "ready" && repair.mayReleaseQueued) {
    release = repair.recentlyTaken === 1 ? t("whatsappSendingSettings.agile.releaseOne") : t("whatsappSendingSettings.agile.releaseMany", { count: repair.recentlyTaken });
  }
  return (
    <>
      <TextField
        ref={codeRef}
        id={id}
        label={t("whatsappSendingSettings.agile.code")}
        autoComplete="off"
        spellCheck={false}
        hint={hint}
        error={setup.codeError?.text(t)}
        value={setup.code}
        onChange={(event) => {
          setup.setCode(event.target.value);
          setup.setCodeError(null);
        }}
        readOnly={busy}
      />
      <div className="g-wa-qr">
        <ol className="g-wa-qr__steps" aria-label={t("whatsappSendingSettings.agile.qrTitle")}>
          <li>{t("whatsappSendingSettings.agile.step1")}</li>
          <li>{t("whatsappSendingSettings.agile.step2")}</li>
          <li>{t("whatsappSendingSettings.agile.step3")}</li>
        </ol>
        <div className="g-wa-qr__actions">
          {openQr}
          {setup.saved && setup.canRepair ? (
            <Button
              variant="ghost"
              onClick={setup.startRepair}
              disabled={busy || repair.phase === "loading"}
              aria-busy={repair.phase === "loading" ? "true" : undefined}
            >
              <Icon name="phone" />
              {t("whatsappSendingSettings.agile.repair")}
            </Button>
          ) : null}
        </div>
        {repair.phase === "loading" ? (
          <p className="g-wa-muted" role="status">
            {t("whatsappSendingSettings.agile.repairing")}
          </p>
        ) : null}
        {repair.phase === "ready" ? (
          <p className="g-wa-muted" role="status">
            {t("whatsappSendingSettings.agile.repairReady")}
          </p>
        ) : null}
        {release !== null ? <Reason tone="warning">{release}</Reason> : null}
        {repair.phase === "failed" ? (
          <Reason tone="warning" role="alert">
            {repair.problem.text(t)}
          </Reason>
        ) : null}
      </div>
      {saving ? (
        <p className="g-wa-muted" role="status">
          {t("whatsappSendingSettings.agile.saving")}
        </p>
      ) : null}
    </>
  );
}

/** Le persone su WhatsApp Agile il cui canale, all'ultimo controllo salvato, non consegnava. */
export function agileDownPeople(people: readonly WhatsAppSenderPerson[]): WhatsAppSenderPerson[] {
  return people.filter((person) => person.sender?.channel === "agile" && person.sender.state === "ready" && person.sender.agileHealth?.healthy === false);
}

/**
 * L'avviso della pagina «Invio dei WhatsApp» (piano M6, Task 27: «l'avviso nella pagina») quando il canale di Agile di qualcuno non
 * consegna, all'ultimo controllo salvato: chi, e che va collegato di nuovo con il codice QR. Lo vede chiunque guardi la pagina. Non dice
 * dove si aprono i suoi WhatsApp: dopo 10 minuti la finestra offre di nuovo l'invio, che ricontrolla (WA-20).
 */
export function AgileDownNotice({ people }: { people: readonly WhatsAppSenderPerson[] }) {
  const t = useT();
  const { locale } = useLocale();
  const down = agileDownPeople(people);
  const [first] = down;
  if (first === undefined) return null;
  const names = new Intl.ListFormat(locale, { type: "conjunction" }).format(down.map((person) => person.displayName));
  return (
    <Reason tone="warning">
      {down.length === 1 ? t("whatsappSendingSettings.agile.downOne", { name: first.displayName }) : t("whatsappSendingSettings.agile.downMany", { names })}
    </Reason>
  );
}
